import type { JsonObject } from "../types/json";

// Padrão: Pages Function no mesmo domínio (/api/cnpj/:cnpj), com cache e fallback.
// VITE_CNPJ_API_BASE_URL=https://publica.cnpj.ws aponta direto para a API pública (útil no `vite dev`).
const API_BASE_URL = (import.meta.env.VITE_CNPJ_API_BASE_URL ?? "/api").replace(/\/$/, "");
const CACHE_TTL_MS = 30 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 20_000;

interface CachedResponse {
  savedAt: number;
  data: JsonObject;
}

export class CnpjApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "CnpjApiError";
  }
}

function cacheKey(cnpj: string): string {
  return `consulta-cnpj:${cnpj}`;
}

function readCache(cnpj: string): JsonObject | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(cnpj));
    if (!raw) return null;

    const cached = JSON.parse(raw) as CachedResponse;
    if (Date.now() - cached.savedAt > CACHE_TTL_MS) {
      sessionStorage.removeItem(cacheKey(cnpj));
      return null;
    }

    return cached.data;
  } catch {
    return null;
  }
}

function writeCache(cnpj: string, data: JsonObject): void {
  try {
    const payload: CachedResponse = { savedAt: Date.now(), data };
    sessionStorage.setItem(cacheKey(cnpj), JSON.stringify(payload));
  } catch {
    // Falha de cache não deve impedir a consulta.
  }
}

async function readErrorMessage(response: Response): Promise<string | undefined> {
  try {
    const payload = (await response.json()) as Record<string, unknown>;
    const candidate = payload.message ?? payload.mensagem ?? payload.error;
    return typeof candidate === "string" ? candidate : undefined;
  } catch {
    return undefined;
  }
}

export async function fetchCnpj(cnpj: string, externalSignal?: AbortSignal): Promise<JsonObject> {
  const cached = readCache(cnpj);
  if (cached) return cached;

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort("timeout"), REQUEST_TIMEOUT_MS);

  const abortFromExternalSignal = () => controller.abort("cancelled");
  externalSignal?.addEventListener("abort", abortFromExternalSignal, { once: true });

  try {
    const response = await fetch(`${API_BASE_URL}/cnpj/${encodeURIComponent(cnpj)}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });

    if (!response.ok) {
      const apiMessage = await readErrorMessage(response);

      if (response.status === 404) {
        throw new CnpjApiError(apiMessage ?? "Nenhuma empresa foi encontrada para este CNPJ.", 404);
      }

      if (response.status === 429) {
        throw new CnpjApiError(
          apiMessage ?? "O limite da API pública foi atingido. Aguarde um minuto e tente novamente.",
          429,
        );
      }

      if (response.status >= 500) {
        throw new CnpjApiError("O serviço de consulta está temporariamente indisponível.", response.status);
      }

      throw new CnpjApiError(apiMessage ?? "Não foi possível concluir a consulta.", response.status);
    }

    const data = (await response.json()) as JsonObject;
    writeCache(cnpj, data);
    return data;
  } catch (error) {
    if (error instanceof CnpjApiError) throw error;

    if (error instanceof DOMException && error.name === "AbortError") {
      if (externalSignal?.aborted) throw new CnpjApiError("Consulta cancelada.");
      throw new CnpjApiError("A consulta demorou mais que o esperado. Tente novamente.");
    }

    throw new CnpjApiError(
      "Não foi possível conectar ao serviço de consulta. Verifique sua internet e tente novamente.",
    );
  } finally {
    window.clearTimeout(timeout);
    externalSignal?.removeEventListener("abort", abortFromExternalSignal);
  }
}
